import { handleError, trackCustomEvent } from "@/lib/newrelic";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

export interface UseFetchOptions {
  enabled?: boolean;
  trackingContext?: Record<string, string>;
  errorSource?: string;
  errorCustomAttributes?: Record<string, string | number | boolean>;
  /**
   * When true, suppress the verbose `[Error Handler]` console output on localhost
   * for both expected (`_fetchError`) and unexpected (`_unexpectedError`) failures.
   * New Relic still receives the full error context.
   */
  quietOnLocalhost?: boolean;
}

interface UseFetchState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
}

type UseFetchAction<T> =
  | { type: "loading" }
  | { type: "success"; payload: T }
  | { type: "error"; payload: Error }
  | { type: "idle" };

export interface UseFetchResult<T> extends UseFetchState<T> {
  refetch: () => Promise<void>;
  abortRequest: () => void;
}

type Fetcher<T> = (
  signal: AbortSignal
) => Promise<{ data: T | null; error: Error | null }>;

type AbortReason = "effectCleanup" | "manual";

interface ActiveRequest {
  abortEventTracked: boolean;
  abortReason?: AbortReason;
  controller: AbortController;
}

export function useFetch<T>(
  fetcher: Fetcher<T>,
  options: UseFetchOptions = {}
): UseFetchResult<T> {
  const {
    enabled = true,
    trackingContext = {},
    errorSource = "api",
    errorCustomAttributes,
    quietOnLocalhost = false,
  } = options;

  const memoizedErrorCustomAttributes = useMemo(
    () => errorCustomAttributes ?? {},
    [errorCustomAttributes]
  );

  const memoizedTrackingContext = useMemo(
    () => trackingContext ?? {},
    [trackingContext]
  );

  const activeRequestRef = useRef<ActiveRequest | null>(null);

  const reducer = (
    state: UseFetchState<T>,
    action: UseFetchAction<T>
  ): UseFetchState<T> => {
    switch (action.type) {
      case "loading":
        return { data: null, isLoading: true, error: null };
      case "success":
        return { data: action.payload, isLoading: false, error: null };
      case "error":
        return { data: null, isLoading: false, error: action.payload };
      case "idle":
        return { data: null, isLoading: false, error: null };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    data: null,
    isLoading: false,
    error: null,
  });

  const abortActiveRequest = useCallback((reason: AbortReason): void => {
    const activeRequest = activeRequestRef.current;
    if (!activeRequest) {
      return;
    }

    activeRequest.abortReason ??= reason;
    activeRequest.controller.abort();
  }, []);

  const abortRequest = useCallback((): void => {
    abortActiveRequest("manual");
  }, [abortActiveRequest]);

  const executeFetch = useCallback(async (): Promise<void> => {
    const request: ActiveRequest = {
      abortEventTracked: false,
      controller: new AbortController(),
    };
    activeRequestRef.current = request;
    dispatch({ type: "loading" });

    const handleCancellation = (): boolean => {
      if (!request.controller.signal.aborted) {
        return false;
      }

      if (!request.abortEventTracked) {
        request.abortEventTracked = true;
        trackCustomEvent("apiRequestAborted", {
          endpoint: memoizedTrackingContext.endpoint || errorSource,
          reason: request.abortReason || "manual",
        });
      }

      if (activeRequestRef.current === request) {
        activeRequestRef.current = null;
        dispatch({ type: "idle" });
      }

      return true;
    };

    try {
      const { data, error } = await fetcher(request.controller.signal);

      if (handleCancellation()) {
        return;
      }

      if (error || !data) {
        const err = error || new Error("No data returned from API");

        handleError(err, {
          source: `${errorSource}_fetchError`,
          context: memoizedTrackingContext,
          customAttributes: memoizedErrorCustomAttributes,
          quietOnLocalhost,
        });

        dispatch({ type: "error", payload: err });
        if (activeRequestRef.current === request) {
          activeRequestRef.current = null;
        }
        return;
      }

      dispatch({ type: "success", payload: data });
      if (activeRequestRef.current === request) {
        activeRequestRef.current = null;
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Unknown error");

      if (handleCancellation()) {
        return;
      }

      handleError(err, {
        source: `${errorSource}_unexpectedError`,
        severity: "high",
        context: memoizedTrackingContext,
        customAttributes: memoizedErrorCustomAttributes,
        quietOnLocalhost,
      });

      dispatch({ type: "error", payload: err });
      if (activeRequestRef.current === request) {
        activeRequestRef.current = null;
      }
    }
  }, [
    fetcher,
    errorSource,
    memoizedTrackingContext,
    memoizedErrorCustomAttributes,
    quietOnLocalhost,
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    executeFetch();

    return () => {
      abortActiveRequest("effectCleanup");
    };
    // We intentionally only depend on `enabled` so that executeFetch
    // is not re-created and re-triggered on every render in tests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    ...state,
    refetch: executeFetch,
    abortRequest,
  };
}
